import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subscription } from 'rxjs';
import { ThemeService } from 'src/app/shared/services/theme.service';

export type SessionTimeoutDialogResult = 'extend' | 'signOut';

export interface SessionTimeoutDialogData {
	remainingSeconds$: Observable<number>;
}

export const SESSION_TIMEOUT_MESSAGE_ID = 'session-timeout-message';

// Announcing every second floods screen readers, so only these milestones are pushed to the live region.
const ANNOUNCED_SECONDS: ReadonlyArray<number> = [60, 30, 10];

@Component({
	selector: 'app-session-timeout-dialog',
	templateUrl: './session-timeout-dialog.component.html',
	styleUrls: ['./session-timeout-dialog.component.scss']
})
export class SessionTimeoutDialogComponent implements OnInit, OnDestroy {

	readonly messageId = SESSION_TIMEOUT_MESSAGE_ID;
	countdown = '';
	announcement = '';

	private subscription: Subscription;

	constructor(
		readonly dialogRef: MatDialogRef<SessionTimeoutDialogComponent, SessionTimeoutDialogResult>,
		@Inject(MAT_DIALOG_DATA) readonly data: SessionTimeoutDialogData,
		public themeService: ThemeService,
		private readonly translateService: TranslateService
	) { }

	ngOnInit(): void {
		this.subscription = this.data.remainingSeconds$.subscribe(seconds => this.setRemaining(seconds));
	}

	onExtend(): void {
		this.dialogRef.close('extend');
	}

	onSignOut(): void {
		this.dialogRef.close('signOut');
	}

	ngOnDestroy(): void {
		this.subscription?.unsubscribe();
	}

	private setRemaining(seconds: number): void {
		this.countdown = formatCountdown(seconds);
		this.announcement = ANNOUNCED_SECONDS.indexOf(seconds) >= 0
			? this.translateService.instant('Session.Warning.Countdown', { time: this.countdown })
			: '';
	}
}

export function formatCountdown(totalSeconds: number): string {
	const safeSeconds = Math.max(0, Math.floor(totalSeconds ?? 0));
	const minutes = Math.floor(safeSeconds / 60);
	const seconds = safeSeconds % 60;
	return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
}
